import React, {useEffect, useRef, useState} from 'react';
import {
  FlatList,
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  TextInputSubmitEditingEventData,
  View,
} from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import Modal from 'react-native-modal';
import {faChevronRight} from '@fortawesome/free-solid-svg-icons/faChevronRight';
import {faCircleXmark} from '@fortawesome/free-solid-svg-icons/faCircleXmark';
import {faLocationCrosshairs} from '@fortawesome/free-solid-svg-icons/faLocationCrosshairs';
import {faSearch} from '@fortawesome/free-solid-svg-icons/faSearch';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {useFocusEffect} from '@react-navigation/native';
import {useDebounce} from 'use-debounce';

import Button from '../../components/ui-controls/Button';
import TextInput from '../../components/ui-controls/form/TextInput';
import Text from '../../components/ui-controls/Text';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {GeocodedSuburb} from '../../models/middleware';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {requestFineLocationAccessPermission} from '../../utils/permissions/permissions';

const SearchBarSearchIcon = () => (
  <View focusable={false} pointerEvents="none">
    <FontAwesomeIcon icon={faSearch} style={styles.icon} />
  </View>
);

const SearchBarCloseIcon = ({onPress}: {onPress: () => void}) => (
  <Pressable onPress={onPress}>
    <FontAwesomeIcon
      icon={faCircleXmark}
      // @ts-ignore
      style={[styles.icon, {marginLeft: 3}]}
    />
  </Pressable>
);

const Separator = () => (
  <View
    style={{
      marginVertical: 4,
      borderBottomWidth: 1,
      borderBottomColor: 'lightgrey',
    }}
  />
);

type SuburbSelectionModalProps = {
  bodyText?: string;
  extraContent?: React.ReactNode;
  isVisible: boolean;
  onCancel?: () => void;
  onSelectSuburb: (suburb: GeocodedSuburb) => void;
  title: string;
};
const SuburbSelectionModal = ({
  bodyText,
  extraContent,
  isVisible,
  onCancel,
  onSelectSuburb,
  title = 'FIND YOUR STORE',
}: SuburbSelectionModalProps) => {
  const [geolocationError, setGeolocationError] = useState<string>('');
  const {shopSelectionStore} = useStores();

  const [isSearching, setSearching] = useState(false);
  const [selectedSuburbValue, setSelectedSuburbValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const handleSelectSuburb = (suburb: GeocodedSuburb) => {
    setSelectedSuburbValue(suburb.locality);
    setSearchText('');

    onSelectSuburb(suburb);
  };

  useEffect(() => {
    async function getSuburbList() {
      if (debouncedSearchText) {
        setSearching(true);
        shopSelectionStore.clearSuburbList();
        await shopSelectionStore.loadSuburbList(debouncedSearchText);
        setSearching(false);
      }
    }
    getSuburbList();
  }, [debouncedSearchText, shopSelectionStore]);

  const handleTextChanged = (text: string) => {
    // Remove anything that's not alphanumeric, space or hyphen.
    const filter = /[^0-9a-zA-Z- ']/gi;
    const newTerm = text.replace(filter, '');
    // Avoid API call for searchText same term
    if (newTerm !== searchText || newTerm === '') {
      setSearchText(newTerm);
      setSelectedSuburbValue('');
      if (newTerm && newTerm.length >= 3) {
        // show indicator immediately, independently of the debouncing
        setSearching(true);
      }
    }
  };

  const handleManualSearchPress = ({
    nativeEvent: {text},
  }: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
    if (text && text.length >= 3) {
      shopSelectionStore.loadSuburbList(text);
    }
  };

  const handleClearText = () => {
    shopSelectionStore.clearSuburbList();
    handleTextChanged('');
  };

  const handleCancel = () => onCancel?.();

  const handleGeolocationRequested = async () => {
    setSearching(true);
    const authorised = await requestFineLocationAccessPermission();
    if (!authorised) {
      console.warn('Geolocation permission not granted');
      setGeolocationError('Geolocation permission not granted');
      setSearching(false);
      return;
    }
    // selectSuburb({"id": "Beerwah QLD 4519", "label": "Beerwah QLD 4519", "lat": "-26.857043", "lon": "152.957162", "value": "Beerwah QLD 4519"});
    Geolocation.getCurrentPosition(
      async ({coords}) => {
        try {
          const suburbs = await shopSelectionStore.reverseGeocode(
            coords.latitude,
            coords.longitude,
          );

          if (suburbs.length === 1) {
            shopSelectionStore.resetLoading();
            handleSelectSuburb(suburbs[0]);
          }

          setSearching(false);
        } catch (err) {
          console.error(err);
          setSearching(false);
          setGeolocationError('Something went wrong, please try again');
        }
      },
      error => {
        // error.code: PERMISSION_DENIED = 1
        setSearching(false);
        setGeolocationError(error.message);
      },
      {enableHighAccuracy: false, timeout: 10000, maximumAge: 10000},
    );
  };

  const renderSuburbListItem = ({item: suburb}: {item: GeocodedSuburb}) => (
    <Pressable onPress={() => handleSelectSuburb(suburb)}>
      <View style={styles.suburbRow}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.suburbText}>
          {`${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`}
        </Text>
        <FontAwesomeIcon
          icon={faChevronRight}
          size={12}
          style={{color: 'lightgrey'}}
        />
      </View>
    </Pressable>
  );

  const renderSearchResults = () => {
    const maxResults = 8;

    if (
      isSearching ||
      shopSelectionStore.suburbListLoading ||
      shopSelectionStore.geocoding
    ) {
      return <Text style={styles.loadingText}>Searching...</Text>;
    }

    if (
      !isSearching &&
      searchText.length >= 3 &&
      !shopSelectionStore.suburbList.length
    ) {
      return (
        <Text style={styles.errorText}>
          No matching suburbs were found. Please try again.
        </Text>
      );
    }

    if (geolocationError) {
      return (
        <View>
          <Text
            style={
              styles.errorText
            }>{`Unable to retrieve current location: ${geolocationError}.`}</Text>
          <Button title="OK" onPress={() => setGeolocationError('')} />
        </View>
      );
    }

    const suburbList = shopSelectionStore.suburbList.slice(0, maxResults);

    return (
      <FlatList
        style={[styles.suburbScrollView]}
        data={suburbList}
        keyExtractor={item => `${item.lat}`}
        renderItem={renderSuburbListItem}
        ItemSeparatorComponent={Separator}
        keyboardShouldPersistTaps="always"
        keyboardDismissMode="on-drag"
      />
    );
  };

  const userInput = searchText || selectedSuburbValue;

  const platformStyles = getPlatformStyles(isWebDesktopScreen);

  const inputRef = useRef(null);

  useFocusEffect(
    React.useCallback(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      return () => null;
    }, []),
  );

  return (
    <Modal
      backdropColor="black"
      backdropOpacity={0.5}
      hasBackdrop
      isVisible={isVisible}
      style={styles.suburbSelectionModal}
      onBackdropPress={handleCancel}
      // no animation
      animationInTiming={1}
      animationOutTiming={1}>
      <View style={platformStyles.suburbSelectionModalContent}>
        <Text style={styles.suburbSelectionModalContentTitle}>{title}</Text>
        {!!bodyText && <Text>{bodyText}</Text>}
        <View />
        <TextInput
          ref={inputRef}
          autoFocus={true}
          leftIcon={<SearchBarSearchIcon />}
          maxLength={80}
          onChangeText={handleTextChanged}
          onSubmitEditing={handleManualSearchPress}
          placeholder="Enter your postcode or suburb"
          renderErrorMessage={false}
          returnKeyType="search"
          rightIcon={<SearchBarCloseIcon onPress={handleClearText} />}
          spellCheck={false}
          value={userInput}
        />
        <Button
          type="clear"
          title="Use your current location"
          onPress={handleGeolocationRequested}
          disabled={shopSelectionStore.geocoding}
          icon={
            <FontAwesomeIcon
              icon={faLocationCrosshairs}
              size={16}
              style={
                isSearching ? styles.locationIconDisabled : styles.locationIcon
              }
            />
          }
          titleStyle={styles.suburbText}
          buttonStyle={{width: 240}}
        />
        {!!extraContent && extraContent}
        {renderSearchResults()}
      </View>
    </Modal>
  );
};

export default SuburbSelectionModal;

const getPlatformStyles = (isWebDesktopScreen: boolean) =>
  StyleSheet.create({
    suburbSelectionModalContent: {
      backgroundColor: 'white',
      paddingHorizontal: 32,
      paddingVertical: 16,
      borderRadius: 4,
      borderWidth: 0.5,
      borderColor: 'grey',
      ...(isWebDesktopScreen
        ? {
            width: '40%',
          }
        : {
            width: '100%',
          }),
    },
  });

const styles = StyleSheet.create({
  suburbScrollView: {
    width: '100%',
    marginTop: 8,
  },
  icon: {
    color: 'lightgrey',
  },
  suburbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 5,
    left: 1,
  },
  suburbText: {
    fontSize: 16,
    marginLeft: 8,
    fontWeight: '400',
  },
  locationIcon: {
    color: theme.colour.uiControls.primary,
    marginRight: 4,
  },
  locationIconDisabled: {
    color: theme.colour.background.button.disabled,
  },
  suburbSelectionModal: {
    alignItems: 'center',
  },
  suburbSelectionModalContentTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
    paddingBottom: 16,
  },
  suburbSelectionModalContentBody: {
    flex: 1,
    alignItems: 'center',
  },
  suburbSelectionModalText: {
    textAlign: 'center',
  },
  loadingText: {
    textAlign: 'center',
    marginTop: 30,
  },
  errorText: {
    marginTop: 10,
  },
});
